import { useEffect, useState } from 'react';
import { fillPattern, framedTitle, type SeoPatternKind } from '@ecom/contracts';
import { BilingualField, ToggleField, type BilingualValue } from '@/components/fields';
import { Section, Segmented } from '@/components/page';
import { useT } from '@/i18n/use-t';
import { useUiStore } from '@/i18n';
import { entitySeoChecks } from '@/lib/seo-checks';
import { storefrontOrigin, storefrontPath } from '@/lib/storefront-url';
import type { Language } from '@/lib/content-pages';
import { Checklist, ScoreRing } from '@/pages/content-pages/seo';
import { useSeoConfigStore } from '@/stores/seo-config.store';
import { useWorkspaceStore } from '@/stores/workspace.store';

/** An item's search result as staff edit it (ADR-095). */
export interface SeoDraft {
  metaTitle: BilingualValue;
  metaDescription: BilingualValue;
  noindex: boolean;
}

export function emptySeo(): SeoDraft {
  return { metaTitle: { ar: '', en: '' }, metaDescription: { ar: '', en: '' }, noindex: false };
}

/** The draft as the API takes it. */
export function seoBody(draft: SeoDraft) {
  return {
    metaTitle: { ar: draft.metaTitle.ar.trim(), en: draft.metaTitle.en.trim() },
    metaDescription: { ar: draft.metaDescription.ar.trim(), en: draft.metaDescription.en.trim() },
    noindex: draft.noindex,
  };
}

/**
 * A product's, category's, brand's or post's search result (ADR-095): its
 * own title and description in both languages — or, left empty, what the
 * store's fill-in pattern or the item's own words will say — a switch to hide
 * it from search engines, the result as Google draws it, and a score.
 */
export function SeoPanel({
  kind,
  path,
  draft,
  onChange,
  names,
  ownWords,
  values = {},
  hasImage,
}: {
  kind: SeoPatternKind | 'post';
  /** The page's store path, `/categories/wallets`. */
  path: string;
  draft: SeoDraft;
  onChange: (next: SeoDraft) => void;
  names: BilingualValue;
  /** The item's own words, which stand in for a description nobody wrote. */
  ownWords: BilingualValue;
  /** What the pattern may name besides the name and the store: category, brand, price, count. */
  values?: Record<string, string | undefined>;
  hasImage: boolean;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [language, setLanguage] = useState<Language>(locale);
  const { config, load } = useSeoConfigStore();
  const store = useWorkspaceStore((state) => state.workspace?.storeName ?? '');

  useEffect(() => {
    void load();
  }, [load]);

  const pattern = (lang: Language, field: 'title' | 'description') => {
    if (kind === 'post') return '';
    const written = config?.patterns?.[kind]?.[field]?.[lang]?.trim();
    return written ? fillPattern(written, { name: names[lang], store, ...values }) : '';
  };
  const resultTitle = (lang: Language) => draft.metaTitle[lang].trim() || pattern(lang, 'title') || names[lang].trim();
  const standIn = (lang: Language) => pattern(lang, 'description') || ownWords[lang].trim();

  const title = framedTitle(config?.titleTemplate?.[language] ?? '', resultTitle(language), store);
  const description = draft.metaDescription[language].trim() || standIn(language);
  const other: Language = language === 'ar' ? 'en' : 'ar';
  const checks = entitySeoChecks({
    language,
    title,
    description: draft.metaDescription[language],
    fallbackDescription: standIn(language),
    otherWritten: Boolean(names[other].trim()),
    hasImage,
    noindex: draft.noindex,
  });
  const origin = storefrontOrigin() ?? '';
  const set = (patch: Partial<SeoDraft>) => onChange({ ...draft, ...patch });

  return (
    <Section title={t('seoEngines')} subtitle={t('seoEnginesHint')} action={<ScoreRing checks={checks} size={48} />}>
      <div className="flex flex-col gap-5">
        <BilingualField
          label={t('metaTitle')}
          hint={t('seoTitleFallbackHint')}
          value={draft.metaTitle}
          limit={120}
          placeholders={{ ar: pattern('ar', 'title') || names.ar, en: pattern('en', 'title') || names.en }}
          onChange={(metaTitle) => set({ metaTitle })}
        />
        <BilingualField
          label={t('metaDescription')}
          hint={t('seoDescriptionFallbackHint')}
          multiline
          rows={3}
          limit={320}
          value={draft.metaDescription}
          placeholders={{ ar: standIn('ar'), en: standIn('en') }}
          onChange={(metaDescription) => set({ metaDescription })}
        />
        <ToggleField label={t('seoHideLabel')} hint={t('seoHideHint')} checked={draft.noindex} onChange={(noindex) => set({ noindex })} />

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-medium text-ink-muted">{t('seoPreviewGoogle')}</span>
            <Segmented
              label={t('seoLanguage')}
              value={language}
              onChange={setLanguage}
              items={[
                { id: 'ar', label: 'العربية' },
                { id: 'en', label: 'English' },
              ]}
            />
          </div>
          <div dir={language === 'ar' ? 'rtl' : 'ltr'} lang={language} className="rounded-card border border-line bg-white p-4 font-[arial,sans-serif] dark:bg-[#1f1f1f]">
            <p dir="ltr" className="truncate text-start text-xs text-[#4d5156] dark:text-[#bdc1c6]">
              {origin}
              {storefrontPath(language, path)}
            </p>
            <p className="mt-1 truncate text-xl leading-snug text-[#1a0dab] dark:text-[#99c3ff]">{title}</p>
            <p className="mt-1 line-clamp-2 text-sm leading-[1.58] text-[#4d5156] dark:text-[#bdc1c6]">{description || t('seoNoDescription')}</p>
          </div>
        </div>

        <Checklist checks={checks} />
      </div>
    </Section>
  );
}
